/**
 * Shadow pass — single-cascade directional shadow map.
 *
 * Allocates a 2048² depth32float texture, then every frame renders both the
 * terrain (vertex-displaced grid) and the buildings (instanced cubes) into it
 * from the sun's POV, depth-only (no fragment shader, no color attachment).
 *
 * The bind group exposed at `bindGroup` / `bindGroupLayout` is the *consumer*
 * group: terrain + buildings sample this depth with a comparison sampler and
 * apply shadow attenuation in their fragment shader.
 *
 * Internal pipelines are depth-only — `createRenderPipeline` from gpu/pipelines
 * always emits a fragment stage, so we use `device.createRenderPipeline` directly.
 */

import { config } from '../config.ts';
import { createEmptyBindGroup, createUniformBuffer, writeUniform } from '../gpu/buffers.ts';
import { mat4LookAt, mat4Multiply, mat4Ortho } from '../util/mat.ts';
import type { Vec3 } from '../util/vec.ts';
import { v3normalize } from '../util/vec.ts';
import type { TerrainGeometry } from './terrain-geometry.ts';
import cubeWgsl from './cube.wgsl?raw';
import shadowWgsl from './shadow.wgsl?raw';

const wgsl = cubeWgsl + '\n' + shadowWgsl;

export interface ShadowPass {
  /** Render terrain + buildings depth from the sun's POV using `sunDir`. Updates the internal sun-camera UBO. */
  draw(encoder: GPUCommandEncoder, sunDir: Vec3): void;
  readonly bindGroup: GPUBindGroup;
  readonly bindGroupLayout: GPUBindGroupLayout;
  destroy(): void;
}

export interface ShadowPassOptions {
  depthFormat: GPUTextureFormat;
  shadowMapSize: number;

  /** For terrain shadow geometry: */
  heightmapView: GPUTextureView;
  /** Shared with the main terrain pass — built once in frame.ts. */
  terrainGeometry: TerrainGeometry;

  /** For building shadow geometry: */
  instanceBuffer: GPUBuffer;
  instanceCountBuffer: GPUBuffer;
  maxInstances: number;

  /** For tree shadow geometry: */
  treeInstanceBuffer: GPUBuffer;
  treeInstanceCountBuffer: GPUBuffer;
  maxTreeInstances: number;

  /** For car shadow geometry: */
  carInstanceBuffer: GPUBuffer;
  /** Fixed; CPU-known count from src/cars. */
  carInstanceCount: number;
}

const SHADOW_UBO_SIZE = 96;            // mat4 + vec4 + vec4
const TERRAIN_UBO_SIZE = 16;           // vec4 params, mirrors render/terrain.ts
const INSTANCE_STRIDE = 32;            // mirrors gen/buildings + render/buildings
const CUBE_VERTEX_COUNT = 36;
const TREE_INSTANCE_STRIDE = 16;       // mirrors gen/trees + render/trees
const TREE_VERTEX_COUNT = 72;
const CAR_INSTANCE_STRIDE = 16;        // mirrors src/cars + render/cars
const INDIRECT_DRAW_BYTES = 16;

export function createShadowPass(device: GPUDevice, opts: ShadowPassOptions): ShadowPass {
  // --- Depth texture (the shadow map itself) ---
  const shadowMap = device.createTexture({
    label: 'shadow.depth',
    size: { width: opts.shadowMapSize, height: opts.shadowMapSize },
    format: opts.depthFormat,
    usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
  });
  const shadowMapView = shadowMap.createView({ label: 'shadow.depth.view' });

  // --- Sun-camera uniform (shared by both internal pipelines at group(0)) ---
  const shadowUbo = createUniformBuffer(device, SHADOW_UBO_SIZE, 'shadow.ubo');
  const shadowUboBgl = device.createBindGroupLayout({
    label: 'shadow.internal.ubo.bgl',
    entries: [{
      binding: 0,
      visibility: GPUShaderStage.VERTEX,
      buffer: { type: 'uniform', minBindingSize: SHADOW_UBO_SIZE },
    }],
  });
  const shadowUboBindGroup = device.createBindGroup({
    label: 'shadow.internal.ubo.bg',
    layout: shadowUboBgl,
    entries: [{ binding: 0, resource: { buffer: shadowUbo } }],
  });

  // --- Consumer-side bind group (sampled by terrain + buildings fragment shaders) ---
  const consumerBgl = device.createBindGroupLayout({
    label: 'shadow.bgl',
    entries: [
      { binding: 0, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'depth', viewDimension: '2d' } },
      { binding: 1, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'comparison' } },
      { binding: 2, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'uniform', minBindingSize: SHADOW_UBO_SIZE } },
    ],
  });

  const compareSampler = device.createSampler({
    label: 'shadow.sampler',
    compare: 'less',
    magFilter: 'linear',
    minFilter: 'linear',
    addressModeU: 'clamp-to-edge',
    addressModeV: 'clamp-to-edge',
  });

  const consumerBindGroup = device.createBindGroup({
    label: 'shadow.bg',
    layout: consumerBgl,
    entries: [
      { binding: 0, resource: shadowMapView },
      { binding: 1, resource: compareSampler },
      { binding: 2, resource: { buffer: shadowUbo } },
    ],
  });

  // === Terrain shadow geometry: shared with the main terrain pass via opts.terrainGeometry. ===

  // Terrain params UBO + heightmap, bound at group(1) of the terrain-shadow pipeline.
  const terrainUbo = createUniformBuffer(device, TERRAIN_UBO_SIZE, 'shadow.terrain.params');
  writeUniform(device, terrainUbo, new Float32Array([
    config.worldSize,
    config.worldHeight,
    config.heightmapSize,
    0,
  ]));

  const terrainBgl = device.createBindGroupLayout({
    label: 'shadow.terrain.bgl',
    entries: [
      {
        binding: 0,
        visibility: GPUShaderStage.VERTEX,
        buffer: { type: 'uniform', minBindingSize: TERRAIN_UBO_SIZE },
      },
      {
        binding: 1,
        visibility: GPUShaderStage.VERTEX,
        texture: { sampleType: 'unfilterable-float', viewDimension: '2d' },
      },
    ],
  });
  const terrainBindGroup = device.createBindGroup({
    label: 'shadow.terrain.bg',
    layout: terrainBgl,
    entries: [
      { binding: 0, resource: { buffer: terrainUbo } },
      { binding: 1, resource: opts.heightmapView },
    ],
  });

  // === Pipelines (depth-only — built directly to omit fragment stage) ===
  const shaderModule = device.createShaderModule({ label: 'shadow.wgsl', code: wgsl });

  const terrainPipelineLayout = device.createPipelineLayout({
    label: 'shadow.terrain.pll',
    bindGroupLayouts: [shadowUboBgl, terrainBgl],
  });

  const terrainPipeline = device.createRenderPipeline({
    label: 'shadow.terrain',
    layout: terrainPipelineLayout,
    vertex: {
      module: shaderModule,
      entryPoint: 'vs_terrain_shadow',
      buffers: [{
        arrayStride: 2 * 4,
        attributes: [{ shaderLocation: 0, offset: 0, format: 'float32x2' }],
      }],
    },
    primitive: { topology: 'triangle-list', cullMode: 'front', frontFace: 'ccw' },
    depthStencil: {
      format: opts.depthFormat,
      depthCompare: 'less',
      depthWriteEnabled: true,
      depthBias: 1,
      depthBiasSlopeScale: 1.5,
    },
  });

  // Buildings shadow pipeline — empty group(1), instance VB at group(0) + vertex_index for cube.
  const buildingsEmpty = createEmptyBindGroup(device, 'shadow.buildings');

  const buildingsPipelineLayout = device.createPipelineLayout({
    label: 'shadow.buildings.pll',
    bindGroupLayouts: [shadowUboBgl, buildingsEmpty.layout],
  });

  const buildingsPipeline = device.createRenderPipeline({
    label: 'shadow.buildings',
    layout: buildingsPipelineLayout,
    vertex: {
      module: shaderModule,
      entryPoint: 'vs_buildings_shadow',
      buffers: [{
        arrayStride: INSTANCE_STRIDE,
        stepMode: 'instance',
        attributes: [
          { shaderLocation: 0, offset: 0,  format: 'float32x3' },
          { shaderLocation: 1, offset: 12, format: 'float32x2' },
          { shaderLocation: 2, offset: 20, format: 'float32' },
          { shaderLocation: 3, offset: 24, format: 'uint32' },
          { shaderLocation: 4, offset: 28, format: 'uint32' }, // flags — must match render/buildings vertex layout
        ],
      }],
    },
    primitive: { topology: 'triangle-list', cullMode: 'back', frontFace: 'ccw' },
    depthStencil: {
      format: opts.depthFormat,
      depthCompare: 'less',
      depthWriteEnabled: true,
      depthBias: 1,
      depthBiasSlopeScale: 1.5,
    },
  });

  // Indirect draw buffer for buildings — instanceCount populated once at startup
  // from the gen-side atomic counter (same trick as render/buildings.ts).
  const buildingsIndirect = device.createBuffer({
    label: 'shadow.buildings.indirect',
    size: INDIRECT_DRAW_BYTES,
    usage: GPUBufferUsage.INDIRECT | GPUBufferUsage.COPY_DST,
  });
  device.queue.writeBuffer(buildingsIndirect, 0, new Uint32Array([CUBE_VERTEX_COUNT, 0, 0, 0]));
  {
    const enc = device.createCommandEncoder({ label: 'shadow.buildings.indirect.populate' });
    enc.copyBufferToBuffer(opts.instanceCountBuffer, 0, buildingsIndirect, 4, 4);
    device.queue.submit([enc.finish()]);
  }

  // === Trees shadow pipeline (mirrors buildings — same shadowUboBgl + empty group(1)) ===
  const treesEmptyBgl = device.createBindGroupLayout({ label: 'shadow.trees.empty.bgl', entries: [] });
  const treesEmptyBg = device.createBindGroup({ label: 'shadow.trees.empty.bg', layout: treesEmptyBgl, entries: [] });

  const treesPipelineLayout = device.createPipelineLayout({
    label: 'shadow.trees.pll',
    bindGroupLayouts: [shadowUboBgl, treesEmptyBgl],
  });

  const treesPipeline = device.createRenderPipeline({
    label: 'shadow.trees',
    layout: treesPipelineLayout,
    vertex: {
      module: shaderModule,
      entryPoint: 'vs_trees_shadow',
      buffers: [{
        arrayStride: TREE_INSTANCE_STRIDE,
        stepMode: 'instance',
        attributes: [
          { shaderLocation: 0, offset: 0,  format: 'float32x3' },
          { shaderLocation: 1, offset: 12, format: 'float32' },
        ],
      }],
    },
    primitive: { topology: 'triangle-list', cullMode: 'none', frontFace: 'ccw' },
    depthStencil: {
      format: opts.depthFormat,
      depthCompare: 'less',
      depthWriteEnabled: true,
      depthBias: 1,
      depthBiasSlopeScale: 1.5,
    },
  });

  const treesIndirect = device.createBuffer({
    label: 'shadow.trees.indirect',
    size: INDIRECT_DRAW_BYTES,
    usage: GPUBufferUsage.INDIRECT | GPUBufferUsage.COPY_DST,
  });
  device.queue.writeBuffer(treesIndirect, 0, new Uint32Array([TREE_VERTEX_COUNT, 0, 0, 0]));
  {
    const enc = device.createCommandEncoder({ label: 'shadow.trees.indirect.populate' });
    enc.copyBufferToBuffer(opts.treeInstanceCountBuffer, 0, treesIndirect, 4, 4);
    device.queue.submit([enc.finish()]);
  }

  // === Cars shadow pipeline (cube geometry, oriented by heading) ===
  const carsEmptyBgl = device.createBindGroupLayout({ label: 'shadow.cars.empty.bgl', entries: [] });
  const carsEmptyBg = device.createBindGroup({ label: 'shadow.cars.empty.bg', layout: carsEmptyBgl, entries: [] });

  const carsPipelineLayout = device.createPipelineLayout({
    label: 'shadow.cars.pll',
    bindGroupLayouts: [shadowUboBgl, carsEmptyBgl],
  });

  const carsPipeline = device.createRenderPipeline({
    label: 'shadow.cars',
    layout: carsPipelineLayout,
    vertex: {
      module: shaderModule,
      entryPoint: 'vs_cars_shadow',
      buffers: [{
        arrayStride: CAR_INSTANCE_STRIDE,
        stepMode: 'instance',
        attributes: [
          { shaderLocation: 0, offset: 0,  format: 'float32x3' },
          { shaderLocation: 1, offset: 12, format: 'float32' },
        ],
      }],
    },
    primitive: { topology: 'triangle-list', cullMode: 'back', frontFace: 'ccw' },
    depthStencil: {
      format: opts.depthFormat,
      depthCompare: 'less',
      depthWriteEnabled: true,
      depthBias: 1,
      depthBiasSlopeScale: 1.5,
    },
  });

  const carsIndirect = device.createBuffer({
    label: 'shadow.cars.indirect',
    size: INDIRECT_DRAW_BYTES,
    usage: GPUBufferUsage.INDIRECT | GPUBufferUsage.COPY_DST,
  });
  device.queue.writeBuffer(
    carsIndirect, 0,
    new Uint32Array([CUBE_VERTEX_COUNT, opts.carInstanceCount, 0, 0]),
  );

  // --- UBO scratch + sun viewproj computation ---
  const uboScratch = new Float32Array(SHADOW_UBO_SIZE / 4);
  const invShadowMapSize = 1 / opts.shadowMapSize;
  const depthBias = 0.001;

  const computeSunViewProj = (sunDir: Vec3): Float32Array => {
    // Normalize, and clamp y to a small positive value if the sun is below the horizon
    // so we still produce a sane (if visually off) shadow matrix at night.
    let dir: Vec3 = v3normalize(sunDir);
    if (dir[1] < 0.05) {
      dir = v3normalize([dir[0], 0.05, dir[2]]);
    }

    const shadowDistance = config.worldSize;
    const eye: Vec3 = [
      dir[0] * shadowDistance,
      dir[1] * shadowDistance,
      dir[2] * shadowDistance,
    ];
    const target: Vec3 = [0, config.worldHeight * 0.3, 0];
    const up: Vec3 = Math.abs(dir[1]) > 0.99 ? [0, 0, 1] : [0, 1, 0];

    const view = mat4LookAt(eye, target, up);
    const half = config.worldSize * 0.6;
    const near = config.worldSize * 0.5;
    const far  = config.worldSize * 1.5;
    const proj = mat4Ortho(-half, half, -half, half, near, far);
    return mat4Multiply(proj, view);
  };

  const updateUbo = (sunDir: Vec3): void => {
    const vp = computeSunViewProj(sunDir);
    uboScratch.set(vp, 0);
    uboScratch[16] = invShadowMapSize;
    uboScratch[17] = depthBias;
    uboScratch[18] = 0;
    uboScratch[19] = 0;
    uboScratch[20] = 0;
    uboScratch[21] = 0;
    uboScratch[22] = 0;
    uboScratch[23] = 0;
    writeUniform(device, shadowUbo, uboScratch);
  };

  const draw: ShadowPass['draw'] = (encoder, sunDir) => {
    updateUbo(sunDir);

    const pass = encoder.beginRenderPass({
      label: 'shadow.pass',
      colorAttachments: [],
      depthStencilAttachment: {
        view: shadowMapView,
        depthClearValue: 1.0,
        depthLoadOp: 'clear',
        depthStoreOp: 'store',
      },
    });

    // Terrain — geometry shared with the main render pass.
    pass.setPipeline(terrainPipeline);
    pass.setBindGroup(0, shadowUboBindGroup);
    pass.setBindGroup(1, terrainBindGroup);
    pass.setVertexBuffer(0, opts.terrainGeometry.vertexBuffer);
    pass.setIndexBuffer(opts.terrainGeometry.indexBuffer, opts.terrainGeometry.indexFormat);
    pass.drawIndexed(opts.terrainGeometry.indexCount);

    // Buildings
    pass.setPipeline(buildingsPipeline);
    pass.setBindGroup(0, shadowUboBindGroup);
    pass.setBindGroup(1, buildingsEmpty.bindGroup);
    pass.setVertexBuffer(0, opts.instanceBuffer);
    pass.drawIndirect(buildingsIndirect, 0);

    // Trees
    pass.setPipeline(treesPipeline);
    pass.setBindGroup(0, shadowUboBindGroup);
    pass.setBindGroup(1, treesEmptyBg);
    pass.setVertexBuffer(0, opts.treeInstanceBuffer);
    pass.drawIndirect(treesIndirect, 0);

    // Cars
    if (opts.carInstanceCount > 0) {
      pass.setPipeline(carsPipeline);
      pass.setBindGroup(0, shadowUboBindGroup);
      pass.setBindGroup(1, carsEmptyBg);
      pass.setVertexBuffer(0, opts.carInstanceBuffer);
      pass.drawIndirect(carsIndirect, 0);
    }

    pass.end();
  };

  return {
    draw,
    bindGroup: consumerBindGroup,
    bindGroupLayout: consumerBgl,
    destroy() {
      shadowMap.destroy();
      shadowUbo.destroy();
      terrainUbo.destroy();
      buildingsIndirect.destroy();
      treesIndirect.destroy();
      carsIndirect.destroy();
    },
  };
}
